/**
 * Client Portal page states
 * Used to control which component is rendered in the main app
 */
export const ClientPortalPage = {
    INITIATE: 0,
    DASHBOARD: 1,
    LOADING: 4
} as const;

export type ClientPortalPageType = typeof ClientPortalPage[keyof typeof ClientPortalPage];

/**
 * Client Portal modal types
 * Used to control which modal is displayed in the dashboard
 */
export const ClientPortalModalType = {
    NONE: '',
    CLIENT_RETAINER: 'clientretainer',
    INFORMATION_SHARING_CONSENT: 'informationsharingconsent',
    ID_VERIFICATION: 'idverification',
    SOURCE_OF_FUNDS: 'sourceoffunds'
} as const;

export type ClientPortalModalTypeValue = typeof ClientPortalModalType[keyof typeof ClientPortalModalType];

export type StateTypeContext = {
    page: number;
    portalId: number | string;
    appId: string;
    processor: any;
    appCode: string;
    progressMessage?: string;
}

export type StateContextType = {
    state: StateTypeContext,
    setState: React.Dispatch<React.SetStateAction<StateTypeContext>>,
    update_resentInvitation: number,
    dashboardFieldsPollVersion: number,
    bumpDashboardFieldsPoll: () => void,
    bumpEmailLogRefresh: () => void,
}

/**
 * Document saving preference options
 * Maps to the XML attributes SaveToHardDisk and SaveUsingDownload
 * These match the UI options shown in the ASP EditUserProfile form
 */
export const DocumentSavingPreference = {
    MYDRIVE: 'MyDrive',
    EXTERNAL_CLOUD_STORAGE: 'External Cloud Storage',
    SPECIFIC_NETWORK: 'Specific Network',
    DOWNLOAD_ONLY: 'Download Only'
} as const;

export type DocumentSavingPreferenceType = typeof DocumentSavingPreference[keyof typeof DocumentSavingPreference];

/**
 * Document saving preference mapping from XML attributes
 */
export type DocumentSavingPreferenceMapping = {
    saveToHardDisk: boolean;
    saveUsingDownload: boolean;
    preference: DocumentSavingPreferenceType;
}

export type PropertyAttributes = {
    swimmingPool: boolean;
    lakeFrontage: boolean;
    drilledWell: boolean;
    privateRoad: boolean;
    accessedViaHighway: boolean;
    condoUnit: boolean;
    hasMultipleUnits: boolean;
    homeIsNew: boolean;
    hasABoutHouse: boolean;
    sharesDriveway: boolean;
    currentlyHasTenants: boolean;
    notOccupilableYearRound: boolean;
    accessedViaWaterOnly: boolean;
    hasAnElevator: boolean;
    purchasedAsRental: boolean;
}

export type CaseClient = {
    name: string;
    email: string;
    // The exact FullnameAttr value the legacy importIdentification matches against
    // (TransfereeNameFull / TransferorNameFull for ON/AB; OnAgreementNameFull for MB/SK/BC/NS).
    // Used to drive auto-link IDV imports without going through the chooser list.
    nameFull?: string;
}

export type ClientMatchEntry = {
    client: CaseClient;
    isDuplicate: boolean;
}

export type ClientMatchMap = Map<string, ClientMatchEntry>;

export type IDVResultEvaluation = {
    outcome: 'Pass' | 'Fail';
    failMessage?: string;
    faceVerificationFailed: boolean;
    faceMismatchMessage?: string;
}

export type IDVInformation = {
    givenName?: string;
    surname?: string;
    sex?: string;
    idType?: string;
    issuingJurisdiction?: string;
    documentNumber?: string;
    dateOfExpiry?: string;
    dateOfBirth?: string;
}

export type VerifiedClient = {
    Entity: string,
    clientIDImages: {
        backImage: string;
        frontImage: string;
        verifyFaceFrame?: string;
    };
    clientIdFraudCheck: {
        failedChecks: string;
        json: string;
        notPerformedChecks: string;
        passedChecks: string;
        score: string;
        totalChecks: string;
        type: string;
        certainty: string;
        expiredCheck?: 'PASS' | 'FAIL' | 'NOT_PERFORMED';
        screenCheck?: 'PASS' | 'FAIL' | 'NOT_PERFORMED';
        photocopyCheck?: 'PASS' | 'FAIL' | 'NOT_PERFORMED';
        expiryDate?: string;
    };
    clientInformation: {
        Name: string;
        Status: string;
        Timestamp: string;
        Type: string;
        id: string;
    };
    FaceVerificationPassed?: boolean;
    skipFaceVerification?: boolean;
}

export type DocumentsFromLawyerResponse = {
    Docs: DocumentFromLawyerResponse[]
}
export type DocumentFromLawyerResponse = {
    Name: string
}

export type RetainerState = {
    retainerRequired: boolean;
    defaultRetainerRequired: boolean;
    textBoxRetainerRequired: boolean;
    uploadRetainer: boolean;
    textBasedRetainer: string | null;
}

export type ConsentFormState = {
    consentFormRequired: boolean;
    defaultConsentFormRequired: boolean;
    textBoxConsentFormRequired: boolean;
    uploadConsentForm: boolean;
    textBasedConsentForm: string | null;
}

export type ClientPortalConsentFormInformation = {
    DefaultConsentRequired: boolean;
    PortalId: number;
    TextBoxConsentRequired: boolean;
    TextBoxConsentContent?: string;
    UploadConsent: boolean;
}

export type ClientPortalRetainerInformation = {
    DefaultRetainerRequired: boolean;
    PortalId: number;
    TextBoxRetainerRequired: boolean;
    TextBoxRetainerString?: string;
    UploadRetainer: boolean;
}

export type SourceOfFunds = {
    Amount: number;
    LenderName: string;
    Source: string;
}

export type CustomField = {
    value: string;
    type: string;
    name: string;
    perClient: boolean;
    fromSet?: boolean;
    id?: string;
    identifier?: string;
    intakeForm?: string | null;
}

export type CustomFieldsResponse = {
    Name: string;
    PortalId: string;
    Type: string;
    Value: string;
    IntakeForm?: string | null;
}[]

export type Place = {
    address_components: AddressComponent[],
    formatted_address: string,
    place_id: string
}

export type AddressComponent = {
    long_name: string,
    short_name: string,
    types: string[]
}

export type IntakeState = {
    propertyType: string;
    propertyAddress: string;
    streetNumber: string;
    streetNumberSuffix: string;
    streetName: string;
    unitType: string;
    unitNumber: string;
    city: string;
    postalCode: string;
    closingDate: any,
    propertyPrice: string;
    mortgageFile: boolean,
    dataLoaded: boolean
    province: string;
}

export type DocumentRequestsModel = {
    documentName: string
}[]

export type DocumentRequestResponseModal = {
    documentRequest: DocumentRequestsModel;
}

export type Buyer = {
    address: string;
    birthDate: Date;
    buyerName: string;
    cellPhoneNumber: string;
    email: string;
    genderCorp: string;
    firstName: string;
    middleName: string;
    lastName: string;
}


export type Seller = {
    address: string;
    birthDate: Date;
    sellerName: string;
    cellPhoneNumber: string;
    email: string;
    genderCorp: string;
}

export type Client = {
    address: string;
    birthDate: Date;
    clientName: string;
    cellPhoneNumber: string;
    email: string;
    genderCorp: string;
}

export type FileModel = {
    buyers: Buyer[];
    clients: Client[];
    sellers: Seller[];
    city: string;
    closingDate: string;
    postalCode: string;
    propertyAddress: string;
    propertyPrice: string;
    propertyType: string;
    streetName: string;
    streetNumber: string;
    streetNumberSuffix: string;
    unitNumber: string;
    unitType: string;
        isNotaryFirm: boolean;
        language: string;
}

export type FileInfoModel = {
    changes: ChangesModel[],
    file: FileModel
    dashboardSelectedOptions: SelectedOptionsModel
}

export type SelectedOptionsModel = {
    clientConnectId: boolean;
    addIntakeFields: boolean;
    retainerAgreement: boolean;
    documentsRequested: boolean;
    sourceOfFunds: boolean;
    remoteClosingCalls: boolean;
}

export type ChangesModel = {
    ChangesSetId: number;
    FieldName: string;
    Id: number;
    NewValue: string;
    OldValue: string;
}

export type ClientInfoModel = {
    Id: number;
    Activated: boolean;
    ActivatedDateTime: string | null;
    DataSubmitted: boolean;
    PortalId: number;
    RetainerAcknowledged: boolean;
    RetainerAcknowledgedDateTime: string | null;
    ConsentAcknowledged: boolean;
    ConsentAcknowledgedDateTime: string | null;
    SourceOfFundsSubmitted: boolean | null;
    Name: string;
}

export type MortgageInformationModel = {
    primaryMortgage: {
        mortgageAmount: string;
        mortgageLender: string;
        firstPaymentDate: string;
        lastPaymentDate: string;
        lenderContactName: string;
        lenderContactEmail: string;
        lenderContactPhone: string;
    },
    secondaryMortgage: {
        mortgageAmount: string;
        mortgageLender: string;
        firstPaymentDate: string;
        lastPaymentDate: string;
        lenderContactName: string;
        lenderContactEmail: string;
        lenderContactPhone: string;
    }
}

export type FileUploadModel = {
    uploadedFile: File;
    fileName: string;
}

export type PortalModel = {
    ActivatedDateTime: string;
    AppId: number;
    CreatedDateTime: string;
    Id: number;
    LawyerId: string;
    PrimaryMortgage: any;
    PurchasePropertyDetails: any;
    RSPActive: boolean;
    RSPScheduledEndTime: any; // todo
    RSPScheduledStartTime: any;
    RSRequestFromClient: any;
    SecondaryMortgage: any;
    Status: boolean;
    retainerRequired: boolean;
}

/**
 * Consent form progress messages for loading states
 * Used during document generation and upload process
 */
export const ConsentFormProgress = {
    INITIALIZING: 'Initializing...',
    USING_EXISTING_FILEDRIVE_CONNECTION: 'Using existing FileDrive connection...',
    CREATING_NEW_FILEDRIVE_CONNECTION: 'Creating new FileDrive connection...',
    WAITING_FOR_DOCUMENT_GENERATION: 'Waiting for document generation...',
    GENERATING_DOCUMENT: 'Generating consent form from master template...',
    UPLOADING_TO_CLIENTCONNECT: 'Uploading consent form to ClientConnect...',
    FINALIZING: 'Finalizing setup...',
    CREATING_PORTAL: 'Creating portal...',
    NOTIFYING_CLIENTS: 'Notifying clients...'
} as const;

export type ConsentFormProgressType = typeof ConsentFormProgress[keyof typeof ConsentFormProgress];